// i18n public API (thin layer).
//
// The translation DATA used to live here as one ~380 KB `as const` trilingual
// object. Because this module is imported (directly or transitively) by ~148
// client modules, that object landed in the SHARED client JS chunk - so an az
// visitor downloaded ru+en strings too.
//
// The data now lives in per-locale maps (./i18n/az, ./i18n/ru, ./i18n/en) and
// reaches the runtime through a registry (./i18n/registry) that this module
// imports WITHOUT statically importing any locale data. The active locale's
// map is registered by:
//   - the server [locale] layout (via ./i18n/server -> loadMessages), and
//   - the client LocaleProvider (which receives that one map as an RSC prop).
//
// The public API is intentionally UNCHANGED so the 148 call sites keep
// working: `Locale`, `TranslationKey`, `t(key, locale)`, and
// `translateVerdict(verdict, locale)` are all still exported from here with
// identical signatures.
import { resolve } from "./i18n/registry";
import type { Locale } from "./i18n-config";
import type { TranslationKey } from "./i18n/keys";

export type { Locale, TranslationKey };

export function t(key: TranslationKey, locale: Locale): string {
  return resolve(key, locale);
}

// Map of backend verdict strings -> translation keys. Static and tiny, so it
// stays inline here (no data is pulled in by keeping it).
const verdictMap: Record<string, TranslationKey> = {
  "Exceptional Deal": "verdict.exceptional",
  "Great Deal": "verdict.great",
  "Good Deal": "verdict.good",
  "Suspiciously Low": "verdict.suspiciousbargain",
  "Needs Verification": "verdict.needsverification",
  "Fair Price": "verdict.fair",
  "Slightly Overpriced": "verdict.slightly",
  "Overpriced": "verdict.overpriced",
  "Heavily Overpriced": "verdict.heavily",
  "Luxury Segment": "verdict.luxury",
  "Suspiciously High": "verdict.suspicioushigh",
};

export function translateVerdict(verdict: string, locale: Locale): string {
  const key = verdictMap[verdict];
  if (!key) return verdict;
  return t(key, locale);
}

/**
 * Backend verdict string -> translation key, or null when unmapped.
 *
 * Server-side callers (metadata, ImageResponse social cards) must NOT
 * use `translateVerdict`: the `t` above reads the registry that the
 * CLIENT path populates per-locale, so on the server it resolves to
 * the bare key ("verdict.slightly" leaked onto a share card that way).
 * Pair this with `t` from "@/lib/i18n/server-t", which registers all
 * three locales at import time.
 */
export function verdictKey(verdict: string): TranslationKey | null {
  return verdictMap[verdict] ?? null;
}
